import { memo, useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBlock } from './Spinner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAccounts, type SubscriptionsState } from '../context/AccountsContext';
import { useStatsAccess } from '../hooks/useStatsAccess';
import { usePayoutsAccess } from '../hooks/usePayoutsAccess';
import { useMobileMenu, useSidebar } from '../context/SidebarContext';
import { api } from '../api/client';
import type { Account, AccountSubscriptionStatus } from '../api/client';
import { isOnDemand } from '../utils/subscription';
import { AccountAvatar } from './AccountAvatar';
import { FanslyLogo, OnlyFansLogo, normalizePlatform } from './PlatformLogo';
import { FeatherIcon } from './FeatherIcon';
import { MenuTab } from './MenuTab';
import { useToolAccess, NO_TOOLS } from '../hooks/useToolAccess';
import { useTeamAvailability } from './Agency/team/TeamTab';
import { offersAny, phoneTabs, sidebarModel, type NavItem } from './sidebarNav';
import { usePageLoading } from '../hooks/usePageLoading';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { useFoldSlide } from '../hooks/useFoldSlide';
import { useCrmHost } from '../crm/CrmHostContext';
import { useUnreadChats, useUnreadChatsFeed } from './unreadChats';
import { CrmAccountTab } from '../crm/CrmAccountSheet';
import { useAccountSearch } from '../hooks/useAccountSearch';
import { SearchClearButton } from './SearchClearButton';
import { useWorkspace, useWorkspaceBadge } from '../workspace/WorkspaceProvider';
import { parseAiAccountRoute } from '../crm/crmRoutes';
import { desktopBridge } from '../desktop/desktopBridge';
import { Lockup } from '../brand/Brand';
import { DownloadAppDialog } from '../desktop/DownloadAppDialog';
import type { CrmAccount } from '../crm/crmAccess';
import { SidebarMenu, type SidebarMenuActions } from './sidebar/SidebarMenu';
import { usePressMenu, type MenuTarget } from './sidebar/pressMenu';
import {
  UNGROUPED_ID,
  addGroup,
  arrange,
  deleteGroup,
  materialize,
  moveAccount,
  moveSection,
  renameGroup,
  sectionOf,
  seedLayout,
  setCollapsed,
  setGroupColor,
  shiftSection,
  sortSection,
  type GroupColor,
  type SidebarLayout,
} from './sidebar/sidebarLayout';
import './Layout.css';

type PlatformFilter = 'all' | 'onlyfans' | 'fansly';
const PLATFORM_FILTERS: PlatformFilter[] = ['all', 'onlyfans', 'fansly'];

/** What an account row needs, whichever product the account comes from. The AI
 *  view lists the console's accounts; the CRM view lists the CRM's (ADR 0019:
 *  two independent gates), enriched with the console's record when it has one. */
interface SidebarRow {
  account_id: string;
  profile_name: string | null;
  username: string | null;
  avatar_url?: string | null;
  platform?: Account['platform'];
  is_enabled: boolean;
  is_logged_in?: boolean;
  /** CRM view only: listed but not on a plan, so its CRM pages show the No-plan lock. */
  plan_locked?: boolean;
}

/** Where an account row goes: a pending account resumes the add-account flow. */
function rowHref(account: SidebarRow, crmMode: boolean, crmHref: (accountId: string) => string): string {
  if (!account.username && !account.is_logged_in) return `/accounts/new?resume=${account.account_id}`;
  return crmMode ? crmHref(account.account_id) : `/account/${account.account_id}`;
}

function toSidebarRow(crmAccount: CrmAccount, consoleAccount?: Account): SidebarRow {
  if (consoleAccount) return crmAccount.plan_locked ? { ...consoleAccount, plan_locked: true } : consoleAccount;
  const platform = normalizePlatform((crmAccount.platform || null) as Account['platform']);
  return {
    account_id: crmAccount.account_id,
    profile_name: crmAccount.profile_name || null,
    username: crmAccount.username || null,
    avatar_url: crmAccount.avatar_url || null,
    platform,
    is_enabled: crmAccount.is_enabled,
    // Not a console account: never show it as a pending connection.
    is_logged_in: true,
    plan_locked: crmAccount.plan_locked,
  };
}

/** One section of the Accounts list as drawn: a group or the ungrouped place. */
interface ListSection {
  id: string;
  name: string;
  color?: GroupColor;
  ungrouped: boolean;
  /** Collapsed as drawn (a search shows every group open). */
  collapsed: boolean;
  rows: SidebarRow[];
  /** Unread chats across the section's accounts, shown while it is collapsed. */
  unread: number;
  /** The ungrouped place has no heading while it leads the list or is empty. */
  showHeading: boolean;
}

/** What is being dragged, or dragged over: an account row or a section heading. */
type DragTarget = { kind: 'row' | 'heading'; id: string };

interface SidebarAccountListProps {
  /** Only sections with something to draw (a heading or rows). */
  sections: ListSection[];
  loaded: boolean;
  emptyText: string;
  consoleIds: Set<string>;
  subscriptionMap: Record<string, AccountSubscriptionStatus>;
  subscriptionsState: SubscriptionsState;
  crmMode: boolean;
  unread: Record<string, number>;
  crmHref: (accountId: string) => string;
  pathname: string;
  canDrag: boolean;
  dragOver: DragTarget | null;
  onDragStart: (e: React.DragEvent, source: DragTarget) => void;
  onDragOver: (e: React.DragEvent, over: DragTarget) => void;
  onDrop: (over: DragTarget) => void;
  onDragEnd: () => void;
  onToggleSection: (sectionId: string) => void;
  onOpenMenu: (target: MenuTarget, x: number, y: number) => void;
  /** The group whose name is being typed, if any. */
  editingId: string | null;
  /** Ends the typing: the new name, or null to keep the old one. */
  onRenameDone: (sectionId: string, name: string | null) => void;
  onNewGroup: () => void;
  /** Where "Add account" goes; omitted when the person can't add one. */
  addAccountTo?: string;
}

/** The Accounts list. Memoised and blind to the rail's width on purpose:
 *  expanding or collapsing only changes a class on the sidebar, so the rows (a
 *  Link, avatar and logo each, for every account) are not re-rendered on each
 *  toggle. The narrow rail fades their text with CSS; the full tooltip says what
 *  the hidden text did. */
const SidebarAccountList = memo(function SidebarAccountList({
  sections,
  loaded,
  emptyText,
  consoleIds,
  subscriptionMap,
  subscriptionsState,
  crmMode,
  unread: unreadMap,
  crmHref,
  pathname,
  canDrag,
  dragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onToggleSection,
  onOpenMenu,
  editingId,
  onRenameDone,
  onNewGroup,
  addAccountTo,
}: SidebarAccountListProps) {
  const { t } = useTranslation('common');
  const pressMenu = usePressMenu(onOpenMenu);

  // The pill on an account row. Only a genuinely planless account says "No plan".
  // On demand accounts are paid per response, so they get no pill — unless billing
  // paused them (wallet ran out), which is worth flagging.
  const getPlanPill = (status?: AccountSubscriptionStatus): { label: string; tip: string; paused: boolean } | null => {
    // Before the statuses arrive (or if they failed) every account would read as "No plan".
    if (subscriptionsState !== 'ok') return null;
    if (isOnDemand(status)) {
      if (!status?.billing_suspended) return null;
      return {
        label: t('layout.onDemandPaused'),
        tip: t('accountView.onDemandPausedTip', { ns: 'dashboard' }),
        paused: true,
      };
    }
    if (status?.active) return null;
    const label = t('layout.noPlan');
    return { label, tip: label, paused: false };
  };

  const renderRow = (account: SidebarRow) => {
    const isDropTarget = dragOver?.kind === 'row' && dragOver.id === account.account_id;
    // Plan pills are the console's business: never on a CRM-only account.
    const consolePill = consoleIds.has(account.account_id) ? getPlanPill(subscriptionMap[account.account_id]) : null;
    // CRM view: an account off a plan is locked there, whoever's row it is (No-plan lock).
    const locked = crmMode && !!account.plan_locked;
    const pill =
      consolePill ?? (locked ? { label: t('layout.noPlan'), tip: t('crmFrame.noPlanTitle'), paused: false } : null);
    // A pending account was created but never connected (no synced profile,
    // not logged in). Clicking it resumes the add-account flow.
    const isPending = !account.username && !account.is_logged_in;
    const selected = pathname.startsWith(`/account/${account.account_id}`);
    const name = account.profile_name || account.username || account.account_id;
    const platform = normalizePlatform(account.platform);
    const platformName = platform === 'fansly' ? t('layout.platformFansly') : t('layout.platformOnlyFans');
    const handle = account.username ? `@${account.username}` : '—';
    const aiTip = account.is_enabled ? t('layout.aiOn') : t('layout.aiOff');
    // OnlyFans' unread chats (ADR 0037), both views: known only while the
    // Keeper's socket for the account is live; Fansly has no source yet.
    const unread = platform === 'fansly' ? undefined : unreadMap[account.account_id];
    const unreadText =
      unread !== undefined
        ? unread > 0
          ? t('layout.unreadChats', { count: unread })
          : t('layout.noNewMessages')
        : null;
    const sub = unreadText ? `${handle} · ${unreadText}` : handle;
    const tip = [name, platformName, sub, crmMode ? null : aiTip, isPending ? t('layout.pending') : pill?.label]
      .filter(Boolean)
      .join(' · ');
    const rowTo = rowHref(account, crmMode, crmHref);
    return (
      <div
        key={account.account_id}
        draggable={canDrag}
        onDragStart={canDrag ? (e) => onDragStart(e, { kind: 'row', id: account.account_id }) : undefined}
        onDragOver={canDrag ? (e) => onDragOver(e, { kind: 'row', id: account.account_id }) : undefined}
        onDrop={canDrag ? () => onDrop({ kind: 'row', id: account.account_id }) : undefined}
        onDragEnd={onDragEnd}
        {...pressMenu({ kind: 'account', id: account.account_id })}
        className={`sb-row-wrap ${isDropTarget ? 'is-drop-target' : ''}`}
      >
        <Link draggable={false} to={rowTo} className={`sb-row ${selected ? 'is-selected' : ''} ${locked ? 'is-plan-locked' : ''}`} title={tip}>
          <span className="sb-avatar">
            <AccountAvatar avatarUrl={account.avatar_url} size={36} />
            <span className="sb-avatar-plat">
              {platform === 'fansly' ? (
                <FanslyLogo size={13} title={platformName} />
              ) : (
                <OnlyFansLogo size={13} title={platformName} />
              )}
            </span>
            {unread !== undefined && unread > 0 && (
              <span className="sb-unread" title={unreadText ?? undefined}>
                {unread > 99 ? '99+' : unread}
              </span>
            )}
            {!crmMode && <span className={`sb-ai-dot ${account.is_enabled ? 'is-on' : ''}`} title={aiTip} />}
          </span>
          <span className="sb-row-body sb-fade">
            <span className="sb-row-text">
              <span className="sb-row-name">{name}</span>
              <span className="sb-row-sub">{sub}</span>
            </span>
            {isPending ? (
              <span className="sb-pill sb-pill--accent" title={t('layout.pendingHint')}>
                {t('layout.pending')}
              </span>
            ) : pill ? (
              <span className={`sb-pill ${pill.paused ? 'sb-pill--warn' : ''}`} title={pill.tip}>
                {pill.label}
              </span>
            ) : null}
          </span>
        </Link>
      </div>
    );
  };

  const renderHeading = (section: ListSection) => {
    const editing = editingId === section.id;
    const isDropTarget = dragOver?.kind === 'heading' && dragOver.id === section.id;
    const name = section.ungrouped ? t('layout.ungrouped') : section.name;
    const count = t('layout.groupCount', { count: section.rows.length });
    const unreadText = section.collapsed && section.unread > 0 ? t('layout.groupUnread', { count: section.unread }) : null;
    const tip = [name, count, unreadText, section.collapsed ? t('layout.expandGroup') : t('layout.collapseGroup')]
      .filter(Boolean)
      .join(' · ');
    const dot = (
      <span className="sb-group-dot" aria-hidden="true">
        <span className="sb-group-dot-n">{section.rows.length}</span>
        {unreadText && <span className="sb-unread sb-group-unread">{section.unread > 99 ? '99+' : section.unread}</span>}
      </span>
    );
    return (
      <div
        key={`h:${section.id}`}
        className={`sb-group ${section.collapsed ? 'is-collapsed' : ''} ${section.ungrouped ? 'is-ungrouped' : ''} ${isDropTarget ? 'is-drop-target' : ''}`}
        data-color={section.color ?? 'none'}
        draggable={canDrag && !editing}
        onDragStart={canDrag ? (e) => onDragStart(e, { kind: 'heading', id: section.id }) : undefined}
        onDragOver={canDrag ? (e) => onDragOver(e, { kind: 'heading', id: section.id }) : undefined}
        onDrop={canDrag ? () => onDrop({ kind: 'heading', id: section.id }) : undefined}
        onDragEnd={onDragEnd}
        {...(editing ? {} : pressMenu({ kind: 'section', id: section.id }))}
      >
        {editing ? (
          <div className="sb-group-head">
            {dot}
            <input
              className="sb-group-input"
              autoFocus
              defaultValue={section.name}
              maxLength={40}
              aria-label={t('layout.groupName')}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={(e) => onRenameDone(section.id, e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') onRenameDone(section.id, null);
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            className="sb-group-head"
            onClick={() => onToggleSection(section.id)}
            aria-expanded={!section.collapsed}
            title={tip}
          >
            {dot}
            <span className="sb-group-name sb-fade">{name}</span>
            <span className="sb-group-count sb-fade">{section.rows.length}</span>
            {unreadText && <span className="sb-group-unread-inline sb-fade">{section.unread > 99 ? '99+' : section.unread}</span>}
            <FeatherIcon name={section.collapsed ? 'chevron-right' : 'chevron-down'} size={13} className="sb-group-chev sb-fade" />
          </button>
        )}
        {!editing && (
          <button
            type="button"
            className="sb-group-more sb-ex-only"
            title={t('layout.groupMenu')}
            aria-label={t('layout.groupMenu')}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              onOpenMenu({ kind: 'section', id: section.id }, r.left, r.bottom + 4);
            }}
          >
            <FeatherIcon name="more-horizontal" size={14} />
          </button>
        )}
      </div>
    );
  };

  // The next account goes at the end of the list: shaped like a row, with a dashed "+" for an avatar.
  const addRow = addAccountTo && (
    <div className="sb-row-wrap">
      <Link
        to={addAccountTo}
        className={`sb-row sb-row--add ${pathname === addAccountTo ? 'is-selected' : ''}`}
        title={t('layout.addNewAccount')}
      >
        <span className="sb-avatar sb-add-av">
          <FeatherIcon name="plus" size={16} />
        </span>
        <span className="sb-row-body sb-fade">
          <span className="sb-row-text">
            <span className="sb-row-name">{t('layout.addAccount')}</span>
            <span className="sb-row-sub">{t('layout.addAccountHint')}</span>
          </span>
        </span>
      </Link>
    </div>
  );

  return (
    <div className="sb-list">
      {!loaded ? (
        <LoadingBlock pad="16px 0" size={18} label={t('loading')} />
      ) : sections.length > 0 ? (
        <>
          {sections.map((section) => (
            <div key={section.id} className="sb-section" data-color={section.color ?? 'none'}>
              {section.showHeading && renderHeading(section)}
              {!section.collapsed && section.rows.map(renderRow)}
            </div>
          ))}
          {addRow}
          <button type="button" className="sb-group-new sb-ex-only" onClick={onNewGroup}>
            <FeatherIcon name="folder-plus" size={14} />
            <span>{t('layout.newGroup')}</span>
          </button>
        </>
      ) : (
        <>
          <div className="sb-empty">{emptyText}</div>
          {addRow}
        </>
      )}
    </div>
  );
});

interface RailSearchProps {
  rows: SidebarRow[];
  /** Where the search button sits, so the panel opens beside it. */
  top: number;
  crmMode: boolean;
  crmHref: (accountId: string) => string;
  onClose: () => void;
}

/** The narrow rail's account search: a panel beside the rail, so finding an
 *  account does not have to widen the sidebar. Enter opens the first match. */
function RailSearch({ rows, top, crmMode, crmHref, onClose }: RailSearchProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  // The Accounts list's search, so the panel reopens with what was typed.
  const [q, setQ] = useAccountSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    const matching = s
      ? rows.filter((a) => `${a.profile_name || ''} ${a.username || ''} ${a.account_id}`.toLowerCase().includes(s))
      : rows;
    return matching.slice(0, 30);
  }, [rows, q]);

  return (
    <>
      <div className="sb-pop-scrim" onClick={onClose} aria-hidden="true" />
      <div className="sb-pop" style={{ top }} role="dialog" aria-label={t('layout.searchAccounts')}>
        <label className="sb-pop-search">
          <FeatherIcon name="search" size={14} />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('layout.searchAccounts')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hits[0]) {
                navigate(rowHref(hits[0], crmMode, crmHref));
                onClose();
              }
            }}
          />
          <SearchClearButton value={q} onClear={() => { setQ(''); inputRef.current?.focus(); }} />
        </label>
        <div className="sb-pop-list">
          {hits.length > 0 ? (
            hits.map((account) => {
              const platform = normalizePlatform(account.platform);
              return (
                <Link key={account.account_id} to={rowHref(account, crmMode, crmHref)} className="sb-pop-row" onClick={onClose}>
                  <AccountAvatar avatarUrl={account.avatar_url} size={26} />
                  {platform === 'fansly' ? <FanslyLogo size={13} /> : <OnlyFansLogo size={13} />}
                  <span className="sb-pop-name">{account.profile_name || account.username || account.account_id}</span>
                  {account.username && <span className="sb-pop-handle">@{account.username}</span>}
                </Link>
              );
            })
          ) : (
            <div className="sb-empty">{t('layout.noAccountsMatch')}</div>
          )}
        </div>
      </div>
    </>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation('common');
  const { user, isAuthenticated, logout } = useAuth();
  const canSeeStats = useStatsAccess();
  const canSeePayouts = usePayoutsAccess();
  const teamAvailable = useTeamAvailability();
  const tools = useToolAccess();
  const { isNavOpen, toggleNav, isRailExpanded, toggleRail, sidebarWidth } = useSidebar();
  const crm = useCrmHost();
  useUnreadChatsFeed();
  const unreadChats = useUnreadChats();
  const workspace = useWorkspace();
  const workspaceBadge = useWorkspaceBadge();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobileMenuOpen: isMobileSidebarOpen, setMobileMenuOpen: setIsMobileSidebarOpen, bottomBarClaimed } = useMobileMenu();
  const isNarrow = useIsNarrow();
  // The phone drawer always shows the full account rail and the menu.
  const expanded = isRailExpanded || isNarrow;
  const navOpen = isNavOpen || isNarrow;
  // The narrow rail's search panel: open at this height, or closed (null).
  const [railSearchTop, setRailSearchTop] = useState<number | null>(null);
  const closeRailSearch = useCallback(() => setRailSearchTop(null), []);
  const mainRef = useRef<HTMLElement>(null);
  useFoldSlide(mainRef, sidebarWidth, !isNarrow);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setRailSearchTop(null);
  }, [location.pathname, location.search, setIsMobileSidebarOpen]);

  // Phones: the tab bar's height, for anything sized to the screen or pinned to
  // its bottom (--shell-tabbar-h). A page with its own bar takes the edge instead.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('has-shell-tabbar', !bottomBarClaimed);
    return () => root.classList.remove('has-shell-tabbar');
  }, [bottomBarClaimed]);

  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileSidebarOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMobileSidebarOpen, setIsMobileSidebarOpen]);

  // Progress bar for page navigation
  usePageLoading();
  const { accounts, loaded: accountsLoaded, subscriptionMap, subscriptionsState } = useAccounts();
  const [sidebarSearch, setSidebarSearch] = useAccountSearch();
  // Platform filter for the accounts list. Only surfaced when the user actually
  // has a Fansly account (below), so OnlyFans-only operators see no new control.
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');

  // Account groups (CONTEXT.md → Account group): the User's own arrangement of the
  // list, saved on the User. Seeded once per User — from the saved layout, or from
  // the old flat custom order — then local edits lead and are pushed to the server.
  const [sidebarLayout, setSidebarLayout] = useState<SidebarLayout>(() => seedLayout(null, null));
  const [layoutUser, setLayoutUser] = useState<string | null>(null);
  useEffect(() => {
    if (!user?.id || layoutUser === user.id) return;
    setLayoutUser(user.id);
    setSidebarLayout(seedLayout(user.sidebar_layout, user.sidebar_account_order));
  }, [user?.id, user?.sidebar_layout, user?.sidebar_account_order, layoutUser]);

  const saveLayoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menu, setMenu] = useState<{ target: MenuTarget; x: number; y: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<DragTarget | null>(null);
  const [dragging, setDragging] = useState<DragTarget | null>(null);

  // The list follows the mode: the AI view shows the console's accounts, the CRM
  // view the CRM's (a login can hold either kind of access, or both).
  const consoleIds = useMemo(() => new Set(accounts.map((a) => a.account_id)), [accounts]);
  const rows = useMemo<SidebarRow[]>(() => {
    if (crm.mode !== 'crm') return accounts;
    const byId = new Map(accounts.map((a) => [a.account_id, a]));
    return crm.crmAccounts.map((c) => toSidebarRow(c, byId.get(c.account_id)));
  }, [accounts, crm.mode, crm.crmAccounts]);

  // Newest first: the order accounts in no group yet take at the top of the ungrouped place.
  const recentIds = useMemo(() => [...rows].reverse().map((a) => a.account_id), [rows]);
  // Every account this login can reach in either view, once both lists are in, so a
  // save can drop accounts the User has lost (one that comes back returns ungrouped).
  const knownIds = useMemo(() => {
    if (!accountsLoaded || (crm.access.state !== 'ok' && crm.access.state !== 'none')) return null;
    return new Set([...consoleIds, ...crm.crmAccounts.map((a) => a.account_id)]);
  }, [accountsLoaded, crm.access.state, consoleIds, crm.crmAccounts]);

  // Every change goes through here: applied to the layout as the User sees it now
  // (new accounts placed), shown at once, and saved ~500ms after the last change.
  // Fire-and-forget — on failure the local arrangement stays (a transient error
  // shouldn't undo what the User just arranged).
  const latest = useRef({ recentIds, knownIds });
  latest.current = { recentIds, knownIds };
  const changeLayout = useCallback((change: (layout: SidebarLayout) => SidebarLayout) => {
    setSidebarLayout((current) => {
      const next = change(materialize(current, latest.current.recentIds, latest.current.knownIds));
      if (saveLayoutTimer.current) clearTimeout(saveLayoutTimer.current);
      saveLayoutTimer.current = setTimeout(() => {
        api.updateMyProfile({ sidebar_layout: next }).catch((err) => {
          console.error('Failed to save sidebar groups:', err);
        });
      }, 500);
      return next;
    });
  }, []);

  // Show the platform filter only when there's a Fansly account to filter to —
  // keeps the sidebar unchanged for OnlyFans-only operators.
  const hasFanslyAccount = useMemo(
    () => rows.some((a) => normalizePlatform(a.platform) === 'fansly'),
    [rows],
  );

  // The narrow rail lists every account: its search and filter live in the
  // expanded rail, so they only apply there.
  const filtering = expanded && (sidebarSearch.trim() !== '' || platformFilter !== 'all');
  const matches = useCallback(
    (a: SidebarRow) => {
      if (platformFilter !== 'all' && normalizePlatform(a.platform) !== platformFilter) return false;
      const q = sidebarSearch.trim().toLowerCase();
      if (!q) return true;
      return `${a.profile_name || ''}\n${a.username || ''}\n${a.account_id}`.toLowerCase().includes(q);
    },
    [platformFilter, sidebarSearch],
  );

  // The list as drawn. A search opens every group and hides those without a match;
  // otherwise a group none of whose accounts this view shows is hidden (it keeps its
  // place), while a group that is simply empty stays, so it can be filled.
  const listSections = useMemo<ListSection[]>(() => {
    const byId = new Map(rows.map((a) => [a.account_id, a]));
    const drawn: ListSection[] = [];
    for (const { section, ids } of arrange(sidebarLayout, recentIds)) {
      const all = ids.flatMap((id) => byId.get(id) ?? []);
      const sectionRows = filtering ? all.filter(matches) : all;
      const ungrouped = section.id === UNGROUPED_ID;
      const hidden = filtering ? sectionRows.length === 0 : !ungrouped && all.length === 0 && section.accounts.length > 0;
      if (hidden) continue;
      // The ungrouped place shows its heading only once it has company above it and
      // something in it — or while dragging, so it can be dropped on or moved.
      const showHeading = !ungrouped || (dragging !== null && !filtering) || (drawn.length > 0 && sectionRows.length > 0);
      if (!showHeading && sectionRows.length === 0) continue;
      drawn.push({
        id: section.id,
        name: section.name ?? '',
        color: section.color,
        ungrouped,
        collapsed: filtering ? false : section.collapsed,
        rows: sectionRows,
        unread: sectionRows.reduce(
          (sum, a) => sum + (normalizePlatform(a.platform) === 'fansly' ? 0 : unreadChats[a.account_id] ?? 0),
          0,
        ),
        showHeading,
      });
    }
    return drawn;
  }, [rows, recentIds, sidebarLayout, filtering, matches, unreadChats, dragging]);

  // Every drawn account in arrangement order, for the narrow rail's search panel.
  const arrangedRows = useMemo(() => listSections.flatMap((s) => s.rows), [listSections]);

  // Opening an account inside a collapsed group opens the group, so the selected
  // row is never hidden. Once per opening: collapsing that group afterwards sticks.
  const openAccountId = crm.target?.accountId ?? parseAiAccountRoute(location.pathname);
  const revealedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!openAccountId || !layoutUser || revealedFor.current === openAccountId) return;
    revealedFor.current = openAccountId;
    const section = sidebarLayout.sections.find((s) => s.accounts.includes(openAccountId));
    if (section?.collapsed) changeLayout((l) => setCollapsed(l, section.id, false));
  }, [openAccountId, layoutUser, sidebarLayout, changeLayout]);

  const nameOf = useCallback(
    (accountId: string) => {
      const a = rows.find((r) => r.account_id === accountId) ?? accounts.find((r) => r.account_id === accountId);
      return a ? a.profile_name || a.username || a.account_id : accountId;
    },
    [rows, accounts],
  );

  const newGroupId = () => `g_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const handleNewGroup = useCallback(() => {
    const id = newGroupId();
    changeLayout((l) => addGroup(l, id, t('layout.newGroupName')));
    setEditingId(id);
  }, [changeLayout, t]);

  const handleRenameDone = useCallback(
    (sectionId: string, name: string | null) => {
      setEditingId(null);
      if (name !== null) changeLayout((l) => renameGroup(l, sectionId, name));
    },
    [changeLayout],
  );

  // In the folded rail a heading unfolds the sidebar onto its group; otherwise it
  // collapses or opens the group.
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const handleToggleSection = useCallback(
    (sectionId: string) => {
      if (!expandedRef.current) {
        toggleRail();
        changeLayout((l) => setCollapsed(l, sectionId, false));
        return;
      }
      changeLayout((l) => setCollapsed(l, sectionId, !l.sections.find((s) => s.id === sectionId)?.collapsed));
    },
    [changeLayout, toggleRail],
  );

  const handleOpenMenu = useCallback((target: MenuTarget, x: number, y: number) => setMenu({ target, x, y }), []);
  const closeMenu = useCallback(() => setMenu(null), []);

  const menuActions = useMemo<SidebarMenuActions>(
    () => ({
      rename: (id) => setEditingId(id),
      color: (id, color) => changeLayout((l) => setGroupColor(l, id, color)),
      sort: (id) => changeLayout((l) => sortSection(l, id, nameOf)),
      shift: (id, delta) => changeLayout((l) => shiftSection(l, id, delta)),
      remove: (id) => {
        const group = sidebarLayout.sections.find((s) => s.id === id);
        const count = group?.accounts.length ?? 0;
        if (count > 0 && !window.confirm(t('layout.deleteGroupConfirm', { name: group?.name, count }))) return;
        changeLayout((l) => deleteGroup(l, id));
      },
      moveAccount: (accountId, sectionId) => changeLayout((l) => moveAccount(l, accountId, sectionId, null)),
      newGroupWith: (accountId) => {
        const id = newGroupId();
        changeLayout((l) => moveAccount(addGroup(l, id, t('layout.newGroupName')), accountId, id, null));
        setEditingId(id);
      },
    }),
    [changeLayout, nameOf, sidebarLayout, t],
  );

  const dragSource = useRef<DragTarget | null>(null);
  const handleDragStart = useCallback((e: React.DragEvent, source: DragTarget) => {
    e.stopPropagation();
    dragSource.current = source;
    setDragging(source);
    // Hide the default browser drag ghost (shows link URL otherwise)
    const ghost = document.createElement('div');
    ghost.style.position = 'fixed';
    ghost.style.top = '-1000px';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  }, []);

  // An account drops on a row (before it) or a heading (top of that section); a
  // section drops on a heading (before it).
  const handleDragOver = useCallback((e: React.DragEvent, over: DragTarget) => {
    const source = dragSource.current;
    if (!source || (source.kind === 'heading' && over.kind === 'row')) return;
    e.preventDefault();
    setDragOver((prev) => (prev?.kind === over.kind && prev.id === over.id ? prev : over));
  }, []);

  const handleDragEnd = useCallback(() => {
    dragSource.current = null;
    setDragging(null);
    setDragOver(null);
  }, []);

  const handleDrop = useCallback(
    (over: DragTarget) => {
      const source = dragSource.current;
      handleDragEnd();
      if (!source || (source.kind === over.kind && source.id === over.id)) return;
      if (source.kind === 'heading') {
        if (over.kind === 'heading') changeLayout((l) => moveSection(l, source.id, over.id));
        return;
      }
      changeLayout((l) => {
        if (over.kind === 'row') return moveAccount(l, source.id, sectionOf(l, over.id), over.id);
        const first = l.sections.find((s) => s.id === over.id)?.accounts.find((id) => id !== source.id) ?? null;
        return moveAccount(l, source.id, over.id, first);
      });
    },
    [changeLayout, handleDragEnd],
  );

  const [downloadOpen, setDownloadOpen] = useState(false);
  // Inside the Desktop client the app is already installed.
  const showDownload = useMemo(() => !desktopBridge(), []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggleRail = () => {
    setRailSearchTop(null);
    toggleRail();
  };

  const openRailSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    const top = event.currentTarget.getBoundingClientRect().top;
    setRailSearchTop((open) => (open === null ? Math.max(8, Math.min(top, window.innerHeight - 380)) : null));
  };

  const userHandle = user?.username || user?.email?.split('@')[0] || '';

  // Mode switcher gates (ADR 0019/0020): on an account page a half is disabled
  // when this login cannot reach that account in the other product; the CRM
  // half is hidden altogether when the login has no CRM access at all.
  const crmRouteAccountId = crm.target?.accountId ?? null;
  const aiRouteAccountId = parseAiAccountRoute(location.pathname);
  const canSwitchToAi = !crmRouteAccountId || consoleIds.has(crmRouteAccountId);
  const canSwitchToCrm =
    crm.access.state !== 'error' &&
    (!aiRouteAccountId || crm.access.state !== 'ok' || crm.hasCrmAccess(aiRouteAccountId));
  const crmSwitchHint =
    crm.access.state === 'error'
      ? t('layout.crmUnavailable')
      : canSwitchToCrm
        ? t('layout.crmModeHint')
        : t('layout.noCrmAccessAccount');

  const nav = sidebarModel({
    hasConsoleAccounts: accountsLoaded ? accounts.length > 0 : null,
    crmOk: crm.access.state === 'ok',
    crmNone: crm.access.state === 'none',
    teamAvailable,
    canSeeStats,
    canSeePayouts,
    canAccessPayments: Boolean(user?.can_access_payments),
    tools: tools
      ? {
          copy: tools.usable.copy,
          bulkMedia: tools.usable.bulk_media,
          audio: tools.usable.audio,
          dynamicLists: tools.usable.dynamic_lists,
          trackingLinks: tools.usable.tracking_links,
          massMessages: offersAny(tools.mass_pages?.mass_messages),
          priorityMassMessages: offersAny(tools.mass_pages?.priority_mass_messages),
          automatedMessages: offersAny(tools.mass_pages?.automated_messages),
          chatAssistant: offersAny(tools.mass_pages?.chat_assistant),
        }
      : { copy: NO_TOOLS.usable.copy, bulkMedia: false, audio: false, dynamicLists: false, trackingLinks: false },
    workspaceEnabled: workspace.enabled,
    workspaceBadge,
  });
  // The menu column is one flat list (handoff 15, 2a): Dashboard, Chat assistant and the Agency
  // pages, a line, then the Tools. Workspace sits at the head of the account rail.
  const navSections = [
    [...(nav.dashboard ? [nav.dashboard] : []), ...(nav.chatAssistant ? [nav.chatAssistant] : []), ...(nav.groups.find((g) => g.id === 'agency')?.items ?? [])],
    nav.groups.find((g) => g.id === 'tools')?.items ?? [],
  ].filter((section) => section.length > 0);

  // On a global page the switch also moves between the two homes (2a): CRM opens
  // Workspace, and AI leaves Workspace for the Dashboard. On an account page it
  // keeps the account (CrmHostContext.switchMode).
  const onWorkspace = location.pathname === '/workspace';
  const switchToAi = () => {
    crm.switchMode('ai');
    if (onWorkspace && nav.dashboard) navigate(nav.dashboard.to);
  };
  const switchToCrm = () => {
    crm.switchMode('crm');
    if (!aiRouteAccountId && !crmRouteAccountId && nav.workspace && !onWorkspace) navigate(nav.workspace.to);
  };

  // Phones: the bottom tab bar keeps Menu (the drawer: accounts, every
  // destination, settings) and the four destinations used most — or, for a
  // login with CRM Accounts, the accounts corner (as on the CRM pages' bar) in
  // Team's place.
  const accountsCorner = crm.crmAccounts.length > 0;
  const tabItems = phoneTabs(nav, accountsCorner);
  const tabActive = tabItems.some((item) => location.pathname === item.to);

  const navLink = (item: NavItem) => {
    const active = location.pathname === item.to;
    return (
      <Link key={item.to} to={item.to} className={`sb-nav-item ${active ? 'is-active' : ''}`} aria-current={active ? 'page' : undefined}>
        <FeatherIcon name={item.icon} size={16} />
        <span className="sb-nav-label">{t(item.labelKey)}</span>
        {item.beta ? <span className="sb-nav-beta">{t('layout.beta')}</span> : null}
        {item.badge ? (
          <span className="sb-nav-badge" title={t('layout.workspaceBadge')}>
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        ) : null}
      </Link>
    );
  };

  const onSettings = location.pathname === '/settings';

  return (
    <div className="app-shell flex min-h-[calc(100*var(--om-vh))] bg-[var(--color-bg-primary)]">
      {isMobileSidebarOpen && (
        <button
          type="button"
          className="mobile-sidebar-backdrop"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-label={t('layout.closeMenu')}
        />
      )}
      {/* Two columns (handoff 15, 2a): the account rail, always there, and the
          menu column beside it. Each opens and closes on its own; toggling only
          flips a class here, so nothing inside re-renders or re-mounts. */}
      <div
        className={`app-sidebar sb ${expanded ? 'is-ex' : ''} ${navOpen ? 'is-nav' : ''} ${isMobileSidebarOpen ? 'is-mobile-open' : ''} ${accountsCorner ? 'has-accounts-corner' : ''}`}
      >
        <aside className="sb-rail" aria-label={t('layout.accounts')}>
          <div className="sb-rhead">
            <button
              type="button"
              className={`sb-menu-btn ${isNavOpen ? 'is-on' : ''}`}
              onClick={toggleNav}
              title={isNavOpen ? t('layout.hideMenu') : t('layout.showMenu')}
              aria-label={isNavOpen ? t('layout.hideMenu') : t('layout.showMenu')}
              aria-expanded={isNavOpen}
              aria-controls="sb-navcol"
            >
              {/* The rail always shows the menu icon; the brand lives in the menu column's head. */}
              <FeatherIcon name={isNavOpen ? 'panel-left-close' : 'menu'} size={16} />
              <span className="sb-menu-lbl sb-fade">{t('layout.menu')}</span>
            </button>
          </div>

          {/* Everything between the head and the foot scrolls as one. */}
          <div className="sb-body">
            {nav.workspace && (
              <div className="sb-rpad sb-work">
                <Link
                  to={nav.workspace.to}
                  className={`sb-ws ${onWorkspace ? 'is-active' : ''}`}
                  title={t('layout.workspaceAllChats')}
                  aria-current={onWorkspace ? 'page' : undefined}
                >
                  <FeatherIcon name="grid" size={16} />
                  <span className="sb-ws-lbl sb-fade">{t('layout.workspace')}</span>
                  {workspaceBadge > 0 && (
                    <span className="sb-ws-badge" title={t('layout.workspaceBadge')}>
                      {workspaceBadge > 99 ? '99+' : workspaceBadge}
                    </span>
                  )}
                </Link>
              </div>
            )}

            {/* Mode switcher (ADR 0020), the head of the account list it changes.
                AI is this console; CRM is the client CRM in the persistent frame.
                Stacked on the narrow rail, side by side on the wide one. Hovering
                the CRM half pre-warms the frame. */}
            <div className={`sb-rpad sb-mode-wrap ${nav.workspace ? '' : 'is-first'}`}>
              <div
                className={`sb-mode ${crm.mode === 'crm' ? 'is-crm' : ''} ${crm.access.state === 'none' ? 'is-single' : ''}`}
                role="group"
                aria-label={t('layout.modeSwitcher')}
              >
                <span className="sb-mode-thumb" aria-hidden="true" />
                <button
                  type="button"
                  className={`sb-mode-btn ${crm.mode === 'ai' ? 'is-active' : ''}`}
                  onClick={switchToAi}
                  disabled={!canSwitchToAi}
                  title={canSwitchToAi ? t('layout.aiModeHint') : t('layout.noAiAccessAccount')}
                  aria-pressed={crm.mode === 'ai'}
                >
                  <FeatherIcon name="sparkles" size={14} />
                  <span className="sb-mode-lbl sb-fade">{t('layout.aiMode')}</span>
                </button>
                {crm.access.state !== 'none' && (
                  <button
                    type="button"
                    className={`sb-mode-btn sb-mode-btn--crm ${crm.mode === 'crm' ? 'is-active' : ''}`}
                    onClick={switchToCrm}
                    onMouseEnter={crm.ensureFrame}
                    onFocus={crm.ensureFrame}
                    disabled={!canSwitchToCrm}
                    title={crmSwitchHint}
                    aria-pressed={crm.mode === 'crm'}
                  >
                    <FeatherIcon name="message-circle" size={14} />
                    <span className="sb-mode-lbl sb-fade">{t('layout.crmMode')}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Accounts toolbar: a search button on the narrow rail, search and the
                platform filter on the wide one. Both stay mounted; their height
                eases between the two. */}
            <div className="sb-swap sb-swap--rail" inert={expanded}>
              <div className="sb-swap-in">
                <div className="sb-rail-tools">
                  <button
                    type="button"
                    className={`sb-rail-btn ${railSearchTop !== null ? 'is-on' : ''}`}
                    onClick={openRailSearch}
                    title={t('layout.searchAccounts')}
                    aria-label={t('layout.searchAccounts')}
                  >
                    <FeatherIcon name="search" size={15} />
                  </button>
                  <span className="sb-rail-sep" />
                </div>
              </div>
            </div>
            <div className="sb-swap sb-swap--ex" inert={!expanded}>
              <div className="sb-swap-in">
                <div className="sb-acc-tools">
                  {/* Search, New group and Add account share one row. */}
                  <div className="sb-search-row">
                    <label className="sb-search">
                      <FeatherIcon name="search" size={13} />
                      <input
                        type="text"
                        placeholder={t('layout.searchAccounts')}
                        value={sidebarSearch}
                        onChange={(e) => setSidebarSearch(e.target.value)}
                      />
                      <SearchClearButton value={sidebarSearch} onClear={() => setSidebarSearch('')} size={13} />
                    </label>
                    <button
                      type="button"
                      className="sb-icon-btn"
                      onClick={handleNewGroup}
                      title={t('layout.newGroup')}
                      aria-label={t('layout.newGroup')}
                    >
                      <FeatherIcon name="folder-plus" size={14} />
                    </button>
                    {isAuthenticated && (
                      <button
                        type="button"
                        className="sb-add-btn"
                        onClick={() => navigate('/accounts/new')}
                        title={t('layout.addNewAccount')}
                        aria-label={t('layout.addNewAccount')}
                      >
                        <FeatherIcon name="plus" size={14} />
                        <span>{t('layout.addShort')}</span>
                      </button>
                    )}
                  </div>

                  {/* Platform filter — only shown when a Fansly account exists */}
                  {hasFanslyAccount && (
                    <div className="sb-seg">
                      <span
                        className="sb-seg-thumb"
                        aria-hidden="true"
                        style={{ transform: `translateX(${PLATFORM_FILTERS.indexOf(platformFilter) * 100}%)` }}
                      />
                      {([
                        { id: 'all', label: t('layout.platformAll'), icon: null },
                        { id: 'onlyfans', label: t('layout.platformOnlyFans'), icon: <OnlyFansLogo size={13} /> },
                        { id: 'fansly', label: t('layout.platformFansly'), icon: <FanslyLogo size={13} /> },
                      ] as const).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setPlatformFilter(item.id)}
                          className={`sb-seg-btn ${platformFilter === item.id ? 'is-active' : ''}`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Accounts list. Its own memoised component: toggling never re-renders the rows. */}
            <SidebarAccountList
              sections={listSections}
              loaded={accountsLoaded}
              emptyText={rows.length > 0 ? t('layout.noAccountsMatch') : t('layout.noAccountsAvailable')}
              consoleIds={consoleIds}
              subscriptionMap={subscriptionMap}
              subscriptionsState={subscriptionsState}
              crmMode={crm.mode === 'crm'}
              unread={unreadChats}
              crmHref={crm.crmHref}
              pathname={location.pathname}
              canDrag={!filtering}
              dragOver={dragOver}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onToggleSection={handleToggleSection}
              onOpenMenu={handleOpenMenu}
              editingId={editingId}
              onRenameDone={handleRenameDone}
              onNewGroup={handleNewGroup}
              addAccountTo={isAuthenticated ? '/accounts/new' : undefined}
            />
            {menu && (
              <SidebarMenu
                target={menu.target}
                x={menu.x}
                y={menu.y}
                layout={materialize(sidebarLayout, recentIds)}
                actions={menuActions}
                onClose={closeMenu}
              />
            )}
          </div>

          <div className="sb-foot">
            <Link to="/settings" className="sb-user" title={`@${userHandle} · ${t('layout.settings')}`}>
              <span className="sb-user-av">
                {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : (userHandle.charAt(0).toUpperCase() || '?')}
              </span>
              <span className="sb-user-name sb-fade">@{userHandle}</span>
            </Link>
          </div>
        </aside>

        {/* The menu column. Its content keeps its width and is clipped while the
            column opens or closes, so nothing in it re-lays out on the way. */}
        <div className="sb-navcol" id="sb-navcol" inert={!navOpen}>
          <div className="sb-navcol-in">
            <div className="sb-nhead">
              <Lockup height={11} className="sb-wordmark" />
              {isNarrow && (
                <button
                  type="button"
                  className="sb-icon-btn"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  title={t('layout.closeMenu')}
                  aria-label={t('layout.closeMenu')}
                >
                  <FeatherIcon name="x" size={18} />
                </button>
              )}
            </div>
            <nav className="sb-nav" aria-label={t('layout.mainMenu')}>
              {navSections.map((section, i) => (
                <div key={i} className="sb-nav-section">
                  {i > 0 && <span className="sb-nav-div" />}
                  {section.map(navLink)}
                </div>
              ))}
            </nav>
            <div className="sb-nfoot">
              <Link
                to="/settings"
                className={`sb-nav-item sb-settings ${onSettings ? 'is-active' : ''}`}
                aria-current={onSettings ? 'page' : undefined}
              >
                <FeatherIcon name="settings" size={15} />
                <span className="sb-nav-label">{t('layout.settings')}</span>
              </Link>
              {showDownload && (
                <button
                  type="button"
                  className="sb-icon-btn sb-signout sb-tip"
                  onClick={() => setDownloadOpen(true)}
                  data-tip={t('layout.downloadApp')}
                  aria-label={t('layout.downloadApp')}
                >
                  <FeatherIcon name="download" size={15} />
                </button>
              )}
              <button
                type="button"
                className="sb-icon-btn sb-signout sb-tip"
                onClick={handleLogout}
                data-tip={t('layout.signOut')}
                aria-label={t('layout.signOut')}
              >
                <FeatherIcon name="log-out" size={15} />
              </button>
            </div>
            {downloadOpen && <DownloadAppDialog onClose={() => setDownloadOpen(false)} />}
          </div>
        </div>

        {/* Tabs at the middle of each column's edge: widen/narrow the rail, close the menu. */}
        <button
          type="button"
          className="sb-tab sb-tab--rail"
          onClick={handleToggleRail}
          title={isRailExpanded ? t('layout.collapseAccounts') : t('layout.expandAccounts')}
          aria-label={isRailExpanded ? t('layout.collapseAccounts') : t('layout.expandAccounts')}
          aria-expanded={isRailExpanded}
        >
          <FeatherIcon name={isRailExpanded ? 'chevron-left' : 'chevron-right'} size={15} />
        </button>
        <button
          type="button"
          className="sb-tab sb-tab--nav"
          onClick={toggleNav}
          title={t('layout.hideMenu')}
          aria-label={t('layout.hideMenu')}
          tabIndex={isNavOpen ? undefined : -1}
        >
          <FeatherIcon name="chevron-left" size={15} />
        </button>

        {railSearchTop !== null && !expanded && (
          <RailSearch
            rows={arrangedRows}
            top={railSearchTop}
            crmMode={crm.mode === 'crm'}
            crmHref={crm.crmHref}
            onClose={closeRailSearch}
          />
        )}
      </div>

      {/* Phones: the bottom tab bar (hidden on desktop by CSS). */}
      {!bottomBarClaimed && (
        <nav className="shell-tabbar" aria-label={t('layout.mainMenu')}>
          <MenuTab
            className="shell-tab"
            pillClassName="shell-tab-pill"
            labelClassName="shell-tab-label"
            label={t('layout.menu')}
            open={isMobileSidebarOpen}
            // Account pages, Settings and the rest live in the menu: it stands for them.
            current={isMobileSidebarOpen || !tabActive}
            onToggle={() => setIsMobileSidebarOpen((open) => !open)}
          />
          {tabItems.map((item) => {
            const active = location.pathname === item.to && !isMobileSidebarOpen;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`shell-tab ${active ? 'is-on' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <span className="shell-tab-pill">
                  <FeatherIcon name={item.icon} size={20} />
                  {item.badge ? (
                    <span className="shell-tab-badge">{item.badge > 99 ? '99+' : item.badge}</span>
                  ) : null}
                </span>
                <span className="shell-tab-label">{item.to === '/dashboard' ? t('layout.home') : item.to === '/chat-assistant' ? t('layout.chatAssistantShort') : t(item.labelKey)}</span>
              </Link>
            );
          })}
          {accountsCorner && (
            <CrmAccountTab
              accountId={aiRouteAccountId ?? undefined}
              classes={{ tab: 'shell-tab', pill: 'shell-tab-pill', label: 'shell-tab-label', badge: 'shell-tab-badge' }}
            />
          )}
        </nav>
      )}

      {/* Main content */}
      <main ref={mainRef} className="app-main flex-1" style={{ marginLeft: sidebarWidth }}>
        {children}
      </main>
    </div>
  );
}
